import type { InstantConfigRepository } from "instant-config";
import { sharedIdPartner } from "./SharedIdPartner";
import { id5Partner } from "./Id5Partner";
import { liveIntentConnectedIdPartner } from "./LiveIntentConnectedIdPartner";
import { UID2Partner } from "./UID2Partner";
import { yahooConnectIdPartner } from "./YahooConnectIdPartner";
import type { PrebidPlugin } from "../PrebidPlugin";

type PrebidIdPartnerName =
  | "icLiveIntentConnectedId"
  | "icPrebidSharedId"
  | "icPrebidId5"
  | "icPrebidUID2"
  | "icPrebidYahooConnectId";

// Name and additional config for each partner
export type PrebidIdPartnerConfig = Pick<
  InstantConfigRepository,
  PrebidIdPartnerName | "icPrebidId5ExternalModule"
>;

export type GetPrebidUserIds = (
  config: PrebidIdPartnerConfig,
) => PrebidUserIds | undefined;

export type GetAsyncPrebidUserIds = (
  config: PrebidIdPartnerConfig,
  plugin: PrebidPlugin,
) => Promise<PrebidUserIds | undefined>;

/**
 * List of all supported Prebid ID partners
 * @see https://docs.prebid.org/dev-docs/modules/index.html#user-id-modules
 */
const prebidIdPartners: GetPrebidUserIds[] = [
  sharedIdPartner,
  id5Partner,
  liveIntentConnectedIdPartner,
  yahooConnectIdPartner,
];

export const extendPrebidConfigWithIdPartners = (
  prebidConfig: PrebidConfig,
  config: PrebidIdPartnerConfig,
): PrebidConfig => {
  const userIds: PrebidUserIds[] = prebidIdPartners
    .map((idPartner) => idPartner(config))
    .filter((idPartner) => idPartner !== undefined);
  prebidConfig.userSync ??= {};
  prebidConfig.userSync.userIds ??= [];
  prebidConfig.userSync.userIds.push(...userIds);
  return prebidConfig;
};

/**
 * List of all supported Prebid ID partners
 * @see https://docs.prebid.org/dev-docs/modules/index.html#user-id-modules
 */
const asyncPrebidIdPartners: GetAsyncPrebidUserIds[] = [UID2Partner];

export const registerAsyncPrebidIdPartners = (
  config: PrebidIdPartnerConfig,
  plugin: PrebidPlugin,
  pbjs: Prebid,
) => {
  asyncPrebidIdPartners.forEach((asyncIdPartner) => {
    asyncIdPartner(config, plugin).then((userIds) => {
      if (!userIds) {
        return;
      }

      const userSyncConfig: PrebidUserSyncConfig = pbjs.getConfig("userSync");

      userSyncConfig.userIds ??= [];
      userSyncConfig.userIds.push(userIds);
      pbjs.mergeConfig({
        userSync: userSyncConfig,
      });
    });
  });
};
